// src/pages/client/Home.jsx
import { useState, useEffect, Fragment, useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  CheckCircleIcon,
  UserIcon,
  CalendarIcon,
  CheckBadgeIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  IdentificationIcon,
  ArrowUpOnSquareIcon,
  DocumentDuplicateIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const tutorialSteps = [
  {
    icon: CheckCircleIcon,
    title: 'Congratulations!',
    content: (
      <div className="space-y-6 text-gray-700">
        <div className="bg-indigo-50 p-4 rounded-lg space-y-4">
          <p>You decided to petition for your Bankruptcy rights.</p>
          <p>The process is easier than you may think to receive a fresh start free from debt.</p>
          <p>
            All you need to do is fill out the required schedules and statements, sign them, and
            file them with the Court. Cisowski Law does all this for you. We just need a few
            documents and your credit report.
          </p>
          <p>
            Once your case is filed you are protected from creditor collections, and to receive
            your discharge (the court order eliminating your debts) you just have a few legal
            duties after filing.
          </p>
        </div>
      </div>
    ),
  },
  {
    title: 'About the Attorney',
    content: (
      <div className="flex flex-col items-center space-y-4 text-gray-700">
        <img
          src="/images/oliver-profile.png"
          alt="Oliver Cisowski"
          className="h-32 w-32 rounded-full object-cover border-2 border-indigo-200"
        />
        <div className="space-y-2 text-center max-w-xs">
          <p>
            My name is <strong>Oliver Cisowski</strong>. I’ve been practicing bankruptcy law since <strong>2018</strong>.
          </p>
          <p>
            Outside of law I enjoy movies, outdoors, and computer programming.
          </p>
          <p>
            I developed this entire system to make bankruptcy simpler and more accessible. If you ever have a question, feel free to text, email, or schedule a chat.
          </p>
        </div>
      </div>
    ),
  },
  {
    icon: CalendarIcon,
    title: 'Overview of the process',
    content: null,
  },
  {
    icon: DocumentCheckIcon,
    title: 'Step 1 of 4: Retain Cisowski Law',
    phase: 'Prefiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <DocumentCheckIcon className="h-6 w-6 text-indigo-600" />
          <p className="text-gray-700">
            Review and electronically sign the retainer agreement and make your down payment.
          </p>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <IdentificationIcon className="h-6 w-6 text-indigo-600" />
          <p className="text-gray-700">
            Once you have me retained, I will start building your file. You can let your creditors know you have an attorney on retainer.
          </p>
        </div>
      </div>
    ),
  },
  {
    icon: IdentificationIcon,
    title: 'Step 2 of 4: Obtain your creditor information',
    phase: 'Prefiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <IdentificationIcon className="h-6 w-6 text-indigo-600" />
          <p className="text-gray-700">
            Pull your credit report under the “Credit Report” tab in your client portal.
          </p>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <IdentificationIcon className="h-6 w-6 text-indigo-600" />
          <p className="text-gray-700">
            Once pulled, review your creditors in the “Creditors” tab and add any that don’t ordinarily show up on your credit report (like medical bills).
          </p>
        </div>
      </div>
    ),
  },
  {
    icon: ArrowUpOnSquareIcon,
    title: 'Step 3 of 4: Upload Required Documents',
    phase: 'Prefiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <ArrowUpOnSquareIcon className="h-6 w-6 text-indigo-600" />
          <p className="text-gray-700">
            Provide the required documents listed in the “Documents” tab. You can upload PDFs or take photos directly in the portal.
          </p>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <ArrowUpOnSquareIcon className="h-6 w-6 text-indigo-600" />
          <p className="text-gray-700">
            Don’t forget to complete your required credit counseling course online.
          </p>
        </div>
      </div>
    ),
  },
  {
    icon: DocumentDuplicateIcon,
    title: 'Step 4 of 4: File your Bankruptcy Petition',
    phase: 'Prefiling Phase',
    content: null,
  },
  {
    icon: ShieldCheckIcon,
    title: 'Your Filed Case',
    content: (
      <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
        <ShieldCheckIcon className="h-6 w-6 text-indigo-600" />
        <div className="space-y-2 text-gray-700">
          <p>
            Once your case is filed, you’re assigned a case number and the court issues an
            <strong> Automatic Stay</strong>.
          </p>
          <p>
            The court notifies your creditors, and all collection activities—including phone calls,
            statements, lawsuits, and garnishments—must stop immediately.
          </p>
        </div>
      </div>
    ),
  },
  {
    icon: CheckBadgeIcon,
    title: 'Duty 1 of 4: Take a course in debtor education',
    phase: 'Postfiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <CheckBadgeIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            Like the course before filing, you will be required to take a second postfiling (exit) course.
          </p>
        </div>
      </div>
    ),
  },
  {
    icon: UserIcon,
    title: 'Duty 2 of 4: Attend the 341 Meeting via ZOOM',
    phase: 'Postfiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <UserIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            You must attend a meeting with the trustee assigned to administer your case.
          </p>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <UserIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            You will be placed under oath and must attest to the truth in your forms about your assets, income, and financial affairs.
          </p>
        </div>
      </div>
    ),
  },
  // Duty 3 for Chapter 7
  {
    icon: CurrencyDollarIcon,
    title: 'Duty 3 of 4: Pay the Filing Fee',
    onlyFor: '7',
    phase: 'Postfiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            Pay the court filing fee of <strong>$338</strong> within <strong>120 days</strong> of filing.
          </p>
        </div>
      </div>
    ),
  },
  // Duty 3 for Chapter 13
  {
    icon: CurrencyDollarIcon,
    title: 'Duty 3 of 4: Begin Making Your Chapter 13 Plan Payment',
    onlyFor: '13',
    phase: 'Postfiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            Your first plan payment must be made within <strong>30 days</strong> of filing. Payments can be via wage order, ACH, or mail.
          </p>
        </div>
      </div>
    ),
  },
  // Duty 4 for Chapter 7
  {
    icon: DocumentCheckIcon,
    title: 'Duty 4 of 4: Reaffirm Secured Debts',
    onlyFor: '7',
    phase: 'Postfiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <DocumentCheckIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            If you wish to keep your car loan, mortgage, or other secured debt, sign a reaffirmation agreement and stay current on payments.
          </p>
        </div>
      </div>
    ),
  },
  // Duty 4 for Chapter 13
  {
    icon: ArrowUpOnSquareIcon,
    title: 'Duty 4 of 4: Upload Annual Tax Returns',
    onlyFor: '13',
    phase: 'Postfiling Phase',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <ArrowUpOnSquareIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            Upload a copy of your tax returns each year so that required refunds are paid into the plan as necessary.
          </p>
        </div>
      </div>
    ),
  },
  // Confirmed Chapter 13 Plan (final slide for Ch 13)
  {
    icon: CheckCircleIcon,
    title: 'Your Confirmed Chapter 13 Plan',
    onlyFor: '13',
    content: (
      <div className="space-y-2">
        <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
          <CheckCircleIcon className="h-6 w-6 text-green-600" />
          <p className="text-gray-700">
            Once your plan is confirmed, continue making payments as agreed. Upon completion, the court will grant your Chapter 13 discharge. Congratulations!
          </p>
        </div>
      </div>
    ),
  },
  // Chapter 7 Discharge (final slide for Ch 7)
  {
    title: 'Your Chapter 7 Discharge',
    onlyFor: '7',
    content: (
      <div className="space-y-6 text-center">
        {/* Animated icon */}
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-green-600 animate-bounce" />

        {/* Message card */}
        <div className="p-6 bg-green-50 rounded-lg shadow">
          <p className="text-gray-700">
            After completing your duties, the court will grant your discharge, eliminating your debts and giving you a fresh start free from debt.
          </p>
        </div>

        {/* Emphasized congratulations */}
        <p className="mt-4 text-2xl font-bold text-green-700 animate-pulse">
          Congratulations!
        </p>
      </div>
    ),
  },
];

export default function ClientHome() {
  const { stepStatus } = useOutletContext();
  
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [chapter, setChapter] = useState(null);
  const [preFilingFees, setPreFilingFees] = useState(null);
  const [caseId, setCaseId] = useState('');
  // Additional progress details
  const [checklistItems, setChecklistItems] = useState([]);
  const [feesPaidPrior, setFeesPaidPrior] = useState(0);
  const [pendingSigners, setPendingSigners] = useState([]);
  const [paymentDue, setPaymentDue] = useState(0);
  const [creditPulledStatus, setCreditPulledStatus] = useState(false);
  const [documentStats, setDocumentStats] = useState({ needed: 0, awaiting: 0, accepted: 0 });
  const [signingDate, setSigningDate] = useState(null);
  // Debtor names state
  const [debtorNames, setDebtorNames] = useState([]);
  // Modal state for schedule chat
  const [showChatModal, setShowChatModal] = useState(false);
  const [showCaseFilingModal, setShowCaseFilingModal] = useState(false);
  const [showCaseFilingWarning, setShowCaseFilingWarning] = useState(false);
  const [showFilingFeeModal, setShowFilingFeeModal] = useState(false);
  // Enhanced tutorial/case data
  const [caseData, setCaseData] = useState(null);
  // Calculate down payment: $500 for Ch7, half of fees (min $100) for Ch13
  const downPayment = chapter === '7'
    ? 500
    : chapter === '13'
      ? preFilingFees / 2
      : 0;
  // Only include slides matching the user's chapter or those without a chapter restriction
  const steps = tutorialSteps.filter(step => {
    return !step.onlyFor || step.onlyFor === chapter;
  });
  const CurrentIcon = steps[currentStep]?.icon;
  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    if (!token || !caseId) return;
    fetch(`${API_URL}/case/${caseId}/checklist/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setChecklistItems(data))
      .catch(err => console.error('Failed to load checklist', err))
  }, [token, caseId]);
  const totalItems = checklistItems.length;

  const openPresigned = async (attachmentId) => {
    if (!attachmentId) return;
    try {
      const res = await fetch(
        `${API_URL}/reaffirmations/attachments/${attachmentId}/presign/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        console.error('Presign failed', await res.text());
        return;
      }

      const data = await res.json();
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Presign error', e);
    }
  };
  const points = checklistItems.reduce((sum, item) => {
    const statuses = item.uploads.map(u => u.status);
    if (statuses.includes("accepted")) return sum + 1;
    if (statuses.includes("awaiting_review")) return sum + 0.5;
    return sum; // Rejected or no uploads
  }, 0);
  
  const progress = totalItems ? Math.round((points / totalItems) * 100) : 0;
  const acceptedCount = checklistItems.filter(item =>
    item.uploads.some(u => u.status === 'accepted')
  ).length;
  const awaitingCount = checklistItems.filter(item =>
    !item.uploads.some(u => u.status === 'accepted') &&
    item.uploads.some(u => u.status === 'awaiting_review')
  ).length;
  const attIdByKind = (r, kind) =>
  (Array.isArray(r?.attachments) ? r.attachments : [])
    .find(a => String(a.kind || "").toLowerCase() === kind)?.id;

  const neededCount = checklistItems.length - acceptedCount - awaitingCount;
  // Post-filing follow-up docs: items with no uploads OR only rejected uploads
  // Ignore the Financial Management certificate item (handled by its own duty card)
  const followupDocItems = Array.isArray(checklistItems)
    ? checklistItems.filter(i => {
        const name = String(i?.name || '').trim().toLowerCase();
        if (name === 'certificate of financial management') return false;

        const uploads = Array.isArray(i?.uploads) ? i.uploads : [];
        // Non-rejected upload means something has been provided
        const hasProvided = uploads.some(u => String(u?.status || '').toLowerCase() !== 'rejected');
        return !hasProvided;
      })
    : [];

  const followupDocsNeededCount = followupDocItems.length;
  const followupDocsComplete = followupDocsNeededCount === 0;
  useEffect(() => {
    fetch(`${API_URL}/user/tutorial-seen/`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
      },
      credentials: 'include'
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setCaseData(data);
          // store case info
          setChapter(data.chapter);
          setPreFilingFees(data.pre_filing_fees);
          setCaseId(data.case_id);
          setFeesPaidPrior(data.fees_paid_prior);
          setSigningDate(data.case_signing_date);
          // show tutorial if not yet seen
          setShowTutorial(!data.tutorial_seen);
        } else {
          // on error, default to showing tutorial
          setShowTutorial(true);
        }
      })
      .catch(() => setShowTutorial(true));
  }, []);

  useEffect(() => {
    if (!caseId) return;

    // Retainer‐status: who still needs to sign
    fetch(`${API_URL}/case/${caseId}/retainer-status/`, { credentials: 'include', headers: {
      Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
    }, })
      .then(res => res.json())
      .then(data => {
        const pending = data.filter(d => !d.signed).map(d => d.debtor_full_name);
        setPendingSigners(pending);
        // Capture debtor names
        setDebtorNames(data.map(d => d.debtor_first_name));
        // Calculate remaining down payment
        const due = Math.max(downPayment - data.reduce((sum, d) => sum + (d.payment_amount || 0), 0), 0);
        // If `payment_amount` not returned, fall back to tutorial feesPaidPrior
        setPaymentDue(Math.max(downPayment - feesPaidPrior, 0));
      });

    // Credit report status


    // Documents status
    fetch(`${API_URL}/api/case/${caseId}/documents-status/`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setDocumentStats({
          needed: data.needed_count || data.needed || 0,
          awaiting: data.awaiting_review_count || data.awaiting_review || 0,
          accepted: data.accepted_count || data.accepted || 0,
        });
      });
  }, [caseId, downPayment, feesPaidPrior]);


  const handleClose = () => {
    // Mark tutorial as seen
    fetch(`${API_URL}/user/tutorial-seen/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
      },
      credentials: 'include',
    });
    setShowTutorial(false);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Format signingDate for display
  const formattedSigningDate = useMemo(() => {
    if (!signingDate) return '';
    const dt = new Date(signingDate);
    const datePart = dt.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const timePart = dt.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${datePart} at ${timePart}`;
  }, [signingDate]);

  // --- Derived helpers for post-filing duties ---
  const isFiled = (caseData?.status || '').toLowerCase() === 'filed' || (caseData?.status || '').toLowerCase() === 'confirmed' || caseData?.is_filed === true;
  const caseNumber = caseData?.case_number || caseData?.caseNumber || '';

  // Filing fee info (Chapter 7 only)
  const filingFeeAmount = Number(caseData?.filing_fee_amount ?? caseData?.filingFeeAmount ?? 0);
  const filingFeesPaid = Number(caseData?.filing_fees_paid ?? caseData?.filingFeesPaid ?? 0);
  const filingFeeBalance = filingFeeAmount > 0 ? Math.max(0, filingFeeAmount - filingFeesPaid) : 0;
  const filingFeeIsPaid = filingFeeAmount === 0 ? true : filingFeesPaid >= filingFeeAmount;

  // Financial Management completion: filed OR certificate uploaded (non-rejected)
  const fmFiled = caseData?.financial_management_course_filed === true;
  const fmChecklistItem = Array.isArray(checklistItems)
    ? checklistItems.find(i => (i?.name || '').toLowerCase() === 'certificate of financial management')
    : null;
  const fmHasNonRejectedUpload = !!(
    fmChecklistItem &&
    Array.isArray(fmChecklistItem.uploads) &&
    fmChecklistItem.uploads.some(u => (u?.status || '') !== 'rejected')
  );
  const fmComplete = fmFiled || fmHasNonRejectedUpload;

  // Meeting info
  const meetingDate = caseData?.meeting_date || caseData?.meetingDate || null;
  const meetingId = caseData?.meeting_id || caseData?.meetingId || null;
  const meetingPasscode = caseData?.meeting_passcode || caseData?.meetingPasscode || null;
  const adminHearing = !!caseData?.admin_hearing;

  const meetingDateObj = useMemo(() => {
    if (!meetingDate) return null;
    const d = new Date(meetingDate);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [meetingDate]);

  const meetingIsPast = !!(meetingDateObj && meetingDateObj.getTime() < Date.now());
  const meetingIsFutureAdmin = !!(meetingDateObj && meetingDateObj.getTime() >= Date.now() && adminHearing);
  const meetingShowsComplete = meetingIsPast || meetingIsFutureAdmin;

  // Deadlines
  const fmDeadline = caseData?.financial_management_deadline || null;
  const reaffirmationDeadline = caseData?.reaffirmation_deadline || null;
  // Reaffirmations
  const reaffirmations = Array.isArray(caseData?.reaffirmations) ? caseData.reaffirmations : [];
  const hasReaffirmations = reaffirmations.length > 0;

  const normBucket = (b) => String(b || '').toLowerCase().trim();
  const normStatus = (s) => String(s || '').toLowerCase().trim();

  const reaffAwaitingCreditor = reaffirmations.filter(r =>
    normBucket(r.bucket) === 'awaiting_from_creditor' || normStatus(r.status) === 'draft'
  );

  const reaffAwaitingSignature = reaffirmations.filter(r =>
    normBucket(r.bucket) === 'awaiting_signature' || ['sent', 'rejected'].includes(normStatus(r.status))
  );

  const reaffCompleted = reaffirmations.filter(r =>
    normBucket(r.bucket) === 'complete' || ['client_uploaded', 'accepted'].includes(normStatus(r.status))
  );

  const reaffAwaitingCreditorCount = reaffAwaitingCreditor.length;
  const reaffAwaitingSignatureCount = reaffAwaitingSignature.length;
  const reaffCompletedCount = reaffCompleted.length;
  const reaffTotalCount = reaffirmations.length;
  const reaffAllCompleted = reaffTotalCount > 0 && reaffCompletedCount === reaffTotalCount;

  const getUploadPath = (r) => {
    // Backend sends upload_url only for awaiting_signature; fall back to token route if needed
    if (r?.upload_url) return r.upload_url;
    if (r?.client_upload_token) return `/client/reaffirm/${r.client_upload_token}`;
    return null;
  };

  // Chapter 13 plan
  const ch13Plan = caseData?.chapter13_plan || null;
  const fmtDateTime = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };
  const fmtDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };


  const addDays = (dateVal, days) => {
    if (!dateVal) return null;
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + days);
    return d;
  };


const dateFiled = caseData?.date_filed || null;
const firstPlanPaymentDeadline = addDays(dateFiled, 25);



const periodPhrase = (period) => {
  const p = String(period || '').toLowerCase();
  if (!p) return '';
  if (p === 'weekly') return 'per week';
  if (p === 'biweekly') return 'every 2 weeks';
  if (p === 'semi_monthly' || p === 'semimonthly') return 'twice a month';
  if (p === 'monthly') return 'per month';
  if (p === 'quarterly') return 'per quarter';
  if (p === 'yearly' || p === 'annual') return 'per year';
  return `per ${p}`;
};

const sourceLabel = (src) => {
  const s = String(src || '').toLowerCase();
  if (!s) return '';
  if (s === 'payroll' || s === 'wage' || s === 'wage_order') return 'Payroll (wage order)';
  if (s === 'direct' || s === 'ach') return 'Direct payment';
  if (s === 'mail') return 'Mail';
  return src;
};

  const cleanCreditorName = (raw) => {
    let s = String(raw || '').trim();
    if (!s) return '';

    // Remove common prefixes
    s = s.replace(/^secured creditor:\s*/i, '');
    s = s.replace(/^creditor:\s*/i, '');

    // Remove trailing "for Case ..." noise
    s = s.replace(/\s+for\s+case\s+[0-9a-f\-]{12,}\.?$/i, '');

    // Final trim
    return s.trim();
  };
  return (
    <>
      {showTutorial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          {/* Background overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30"
            onClick={handleClose}
          />
          {/* Modal content */}
          <div className="bg-white rounded-lg max-w-md mx-auto p-6 space-y-4 relative z-10">
            {steps[currentStep]?.phase && (
              <div className="text-sm uppercase font-semibold text-indigo-600 mb-2">
                {steps[currentStep]?.phase}
              </div>
            )}
            <div className="flex items-center justify-center mb-4">
              {CurrentIcon && (
                <CurrentIcon className="h-8 w-8 text-indigo-600 mr-2" />
              )}
              <h3 className="text-xl font-bold">
                {steps[currentStep]?.title}
              </h3>
            </div>
            <div>
              {steps[currentStep]?.title === 'Step 1 of 4: Retain Cisowski Law' ? (
                <div className="space-y-2">
                  <div className="text-xs uppercase font-semibold text-indigo-600">Prefiling Phase</div>
                  <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
                    <DocumentCheckIcon className="h-6 w-6 text-indigo-600" />
                    <p className="text-gray-700">
                      Review and electronically sign the retainer agreement and make your down payment of <strong>${downPayment.toFixed(2)}</strong>.
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow flex items-start space-x-4">
                    <IdentificationIcon className="h-6 w-6 text-indigo-600" />
                    <p className="text-gray-700">
                      Once you have me retained, I will start building your file. You can let your creditors know you have an attorney on retainer.
                    </p>
                  </div>
                </div>
              ) : steps[currentStep]?.title === 'Step 4 of 4: File your Bankruptcy Petition' ? (
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow">
                    <h4 className="text-lg font-semibold text-gray-800">Schedule Your Filing</h4>
                    <p className="text-gray-700 mt-1">
                      Use the “Schedule” tab to set up your in‑office filing appointment.
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow">
                    <h4 className="text-lg font-semibold text-gray-800">Remaining Balance</h4>
                    <p className="text-gray-700 mt-1">
                      Your down payment: <strong>${downPayment.toFixed(2)}</strong><br/>
                      Remaining fees due: <strong>${(preFilingFees - downPayment).toFixed(2)}</strong>
                    </p>
                    <p className="text-gray-600 mt-2">
                      Please ensure the remaining balance is paid before or at your appointment.
                    </p>
                  </div>
                </div>
              ) : currentStep === 2 ? (
                <div className="space-y-6">
                  {/* Prefiling Phase */}
                  <div className="p-4 bg-indigo-50 rounded-lg flex items-start space-x-4">
                    <CalendarIcon className="h-8 w-8 text-indigo-600" />
                    <div>
                      <h4 className="text-lg font-semibold text-indigo-700">Prefiling Phase</h4>
                      <p className="text-gray-600">
                        Collaborate with me to prepare and organize all your required forms. You must pay your attorney fees{' '}
                        <strong>${preFilingFees?.toFixed(2)}</strong> before filing.
                      </p>
                    </div>
                  </div>
                  {/* Postfiling Phase */}
                  {chapter === '7' && (
                    <div className="p-4 bg-green-50 rounded-lg flex items-start space-x-4">
                      <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      <div>
                        <h4 className="text-lg font-semibold text-green-700">Postfiling Phase</h4>
                        <p className="text-gray-600">
                          Fulfill the court‑ordered duties necessary to receive your discharge.
                        </p>
                      </div>
                    </div>
                  )}
                  {chapter === '13' && (
                    <div className="p-4 bg-green-50 rounded-lg flex items-start space-x-4">
                      <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      <div>
                        <h4 className="text-lg font-semibold text-green-700">Postfiling Phase</h4>
                        <p className="text-gray-600">
                          Fulfill the court‑ordered duties necessary to confirm your plan.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                steps[currentStep]?.content
              )}
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={nextStep}
                className="px-3 py-1 bg-indigo-600 text-white rounded"
              >
                {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="p-4">
        <h1 className="text-2xl font-bold text-indigo-700">
          Welcome{debtorNames.length > 0 ? `, ${debtorNames.join(' and ')}` : ''}!
        </h1>
      </div>
      <div className="p-4 bg-white rounded-lg mt-4 shadow text-center">
        <p className="text-gray-700 mb-2">If you ever have any questions, reach out:</p>
        <div className="flex justify-center space-x-2 flex-wrap">
          <a
            href="sms:4233424722"
            className="inline-block px-3 py-1 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 transition"
          >
            Text Us
          </a>
          <a
            href="mailto:oliver@cisowskilaw.com"
            className="inline-block px-3 py-1 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 transition"
          >
            Email Us
          </a>
          <button
            onClick={() => setShowChatModal(true)}
            className="inline-block px-3 py-1 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 transition"
          >
            Schedule Chat
          </button>
        </div>
        {showCaseFilingWarning && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm text-center space-y-4">
              <p className="text-gray-800">
                You must submit all documents and the attorney must accept them before you can schedule your case filing appointment.
              </p>
              <button
                onClick={() => setShowCaseFilingWarning(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                OK
              </button>
            </div>
          </div>
        )}
        {showChatModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white w-11/12 md:w-3/4 lg:w-1/2 h-[80vh] rounded-lg shadow-lg relative">
              <button
                onClick={() => setShowChatModal(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
              <iframe
                src="https://outlook.office.com/book/CisowskiLawQuickChat@NETORGFT5747048.onmicrosoft.com/?ismsaljsauthenabled"
                title="Schedule Chat"
                className="w-full h-full rounded-b-lg"
              />
            </div>
          </div>
        )}
         {showCaseFilingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white w-11/12 md:w-3/4 lg:w-1/2 h-[80vh] rounded-lg shadow-lg relative">
              <button
                onClick={() => setShowCaseFilingModal(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
              <iframe
                src="https://outlook.office.com/owa/calendar/CisowskiLawCaseFilingAppointment@NETORGFT5747048.onmicrosoft.com/bookings/"
                title="Schedule Chat"
                className="w-full h-full rounded-b-lg"
              />
            </div>
          </div>
        )}
              {showFilingFeeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-11/12 md:w-3/4 lg:w-1/2 h-[80vh] rounded-lg shadow-lg relative">
            <button
              onClick={() => setShowFilingFeeModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
              <iframe
                src={`/filing-fee/${caseId}/${Number(filingFeeAmount).toFixed(2)-Number(filingFeesPaid).toFixed(2)}`}
                title="Pay Filing Fee"
                className="w-full h-full rounded-b-lg"
              />
          </div>
        </div>
      )}
        <div className="mt-0 p-4 text-right">
          <p className="italic text-gray-600">Sincerely,</p>
          <p className="text-xl font-semibold text-indigo-700">Oliver Cisowski</p>
        </div>
      </div>
      {!isFiled && (
    <div className="p-4">
        <h2 className="text-xl font-bold mb-4">Your Case Progress</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Retainer */}
          <Link
            to="/client/dashboard/retainer"
            className="relative flex flex-col items-start p-6 bg-gray-50 border-l-4 border-indigo-600 rounded-lg shadow-sm hover:shadow-lg transition-all"
          >
            {stepStatus.retainer && (
              <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                <CheckCircleIcon className="h-12 w-12 text-green-700" />
              </div>
            )}
            <div className="flex items-center mb-2">
              {stepStatus.retainer ? (
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              ) : (
                <DocumentCheckIcon className="h-6 w-6 text-gray-400" />
              )}
              <span className="ml-2 text-lg font-semibold text-gray-800">Retainer</span>
            </div>
            <p className="text-sm text-gray-600">
              {stepStatus.retainer ? (
                <strong>All signers complete</strong>
              ) : (
                <>
                  <strong>Signatures Pending</strong>: {pendingSigners.join(', ') || 'you'}
                </>
              )}
            </p>
            <p className="text-sm text-gray-600">
            <strong>Down payment due</strong>: ${paymentDue.toFixed(2)}
            </p>
          </Link>
          {/* Creditor Info */}
          <Link
            to="/client/dashboard/oneview"
            className="relative flex flex-col items-start p-6 bg-gray-50 border-l-4 border-indigo-600 rounded-lg shadow-sm hover:shadow-lg transition-all"
          >
            {stepStatus.creditors && (
              <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                <CheckCircleIcon className="h-12 w-12 text-green-700" />
              </div>
            )}
            <div className="flex items-center mb-2">
              {stepStatus.creditors ? (
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              ) : (
                <IdentificationIcon className="h-6 w-6 text-gray-400" />
              )}
              <span className="ml-2 text-lg font-semibold text-gray-800">Creditor Info</span>
            </div>
            <p className="text-sm text-gray-600">
              {stepStatus.creditors ? 'Report pulled' : 'Report pending'}
            </p>
          </Link>
          {/* Document Upload */}
          <Link
            to="/client/dashboard/documents"
            className="relative flex flex-col items-start p-6 bg-gray-50 border-l-4 border-indigo-600 rounded-lg shadow-sm hover:shadow-lg transition-all"
          >
            {stepStatus.documents && (
              <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                <CheckCircleIcon className="h-12 w-12 text-green-700" />
              </div>
            )}
            <div className="flex items-center mb-2">
              {stepStatus.documents ? (
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              ) : (
                <ArrowUpOnSquareIcon className="h-6 w-6 text-gray-400" />
              )}
              <span className="ml-2 text-lg font-semibold text-gray-800">Document Upload</span>
            </div>
            <p className="text-sm text-gray-600">
              <strong>Needed</strong>: {neededCount}, <strong>Awaiting review</strong>: {awaitingCount}, <strong>Accepted</strong>: {acceptedCount}
            </p>
          </Link>
         {/* Sign & File (opens schedule modal) */}
         
         <div
           onClick={() => {
              // only allow scheduling if no docs are needed or awaiting review
              if (neededCount === 0 && awaitingCount === 0) {
                setShowCaseFilingModal(true);
              } else {
                setShowCaseFilingWarning(true);
              }
            }}
           className="relative flex flex-col items-start p-6 bg-gray-50 border-l-4 border-indigo-600 rounded-lg shadow-sm hover:shadow-lg transition-all cursor-pointer"
         >
           {stepStatus.filed && (
             <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
               <CheckCircleIcon className="h-12 w-12 text-green-700" />
             </div>
           )}
           <div className="flex items-center mb-2">
             {stepStatus.filed ? (
               <CheckCircleIcon className="h-6 w-6 text-green-500" />
             ) : (
               <ClipboardDocumentListIcon className="h-6 w-6 text-gray-400" />
             )}
             <span className="ml-2 text-lg font-semibold text-gray-800">Sign & File</span>
           </div>
           {signingDate ? (
             <p className="text-sm text-gray-600">
               Your case signing appointment is on <strong>{formattedSigningDate}</strong>! Make sure to pay the rest of your attorney fees <strong>(${preFilingFees - feesPaidPrior})</strong> before or at this appointment and review your creditors!{' '}
               <span className="italic text-xs">(Click to reschedule)</span>
             </p>
           ) : (
             <p className="text-sm text-gray-600">
               Schedule your case filing appointment, pay the balance of your fees, and make a final review of your creditors.{' '}
               <span className="italic text-xs">(Click to schedule)</span>
             </p>
           )}
         </div>
        </div>
      </div>
      )}

      {/* --- Post-Filing Section --- */}
      {isFiled && (
        <div className="p-4">
          <h2 className="text-xl font-bold mb-2">Post-Filing Requirements</h2>
          <p className="text-sm text-gray-700 mb-4">
            Now that your case is filed, you must complete these legal duties in order to{' '}
            {chapter === '13'
              ? 'get your Chapter 13 plan confirmed by the Court'
              : 'receive your Chapter 7 discharge'}
            .
          </p>

          {/* Slim filed card */}
          <div className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-left shadow-sm">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-semibold text-green-800">✅ Case Filed</div>
                <div className="text-sm text-green-900">
                  Case number: <span className="font-semibold">{caseNumber || 'Pending assignment'}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Link
                    to="/client/dashboard/forms"
                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-green-700 text-white hover:bg-green-800"
                  >
                    View Filed Paperwork
                  </Link>
                </div>
              </div>
              {caseData?.date_filed && (
                <div className="text-xs text-green-800">Filed: {fmtDate(caseData.date_filed)}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Duty: Upload follow-up documents before the meeting */}
            {followupDocsNeededCount > 0 && (
            <div className="relative p-5 bg-white rounded-lg shadow border-l-4 border-green-600">
              {followupDocsComplete && (
                <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                  <CheckCircleIcon className="h-12 w-12 text-green-700" />
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Upload Follow‑Up Documents</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {followupDocsComplete ? (
                      'All requested documents have been uploaded.'
                    ) : (
                      <>Documents still needed: <strong>{followupDocsNeededCount}</strong></>
                    )}
                    {meetingDate ? <> • Please upload before: <strong>{fmtDateTime(meetingDate)}</strong></> : null}
                  </p>

                  {!followupDocsComplete && followupDocItems.length > 0 && (
                    <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                      {followupDocItems.slice(0, 6).map((i) => (
                        <li key={i.id}>{i.name}</li>
                      ))}
                      {followupDocItems.length > 6 && (
                        <li className="text-gray-500">…and {followupDocItems.length - 6} more</li>
                      )}
                    </ul>
                  )}
                </div>
                <ArrowUpOnSquareIcon className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm text-gray-700 mt-3">
                The trustee may require additional documents before your next scheduled meeting. Please upload anything we request as soon as possible.
              </p>
              <div className="mt-3">
                <Link
                  to="/client/dashboard/documents"
                  className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Go to Documents
                </Link>
              </div>
            </div>
            )}
            {/* Duty: Filing fee (Chapter 7 only, hide if already paid) */}
                      {chapter === '7' && (
            <div className="relative p-5 bg-white rounded-lg shadow border-l-4 border-green-600">
              {filingFeeIsPaid && (
                <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                  <CheckCircleIcon className="h-12 w-12 text-green-700" />
                </div>
              )}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Pay Filing Fee</h3>
                                      <p className="text-sm text-gray-600 mt-1">
                    Paid so far: <strong>${Number(filingFeesPaid).toFixed(2)}</strong> • Balance due: <strong>${Number(filingFeeBalance).toFixed(2)}</strong>
                  </p>
                  </div>
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-sm text-gray-700 mt-3">
                  If you paid us, we will submit it to the court. If you are paying directly, keep your receipt.
                </p>
                              <div className="mt-3">
                <button
                  onClick={() => setShowFilingFeeModal(true)}
                  disabled={filingFeeIsPaid}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded ${filingFeeIsPaid ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                >
                  Pay Filing Fee
                </button>
              </div>
              </div>
            )}

            {/* Duty: Financial Management (Debtor Education) course (hide if complete) */}
            <div className="relative p-5 bg-white rounded-lg shadow border-l-4 border-green-600">
            {fmComplete && (
              <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                <CheckCircleIcon className="h-12 w-12 text-green-700" />
              </div>
            )}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Take Second Course</h3>
                    <p className="text-sm text-gray-600 mt-1">
                    {fmComplete ? 'Certificate received.' : 'Upload your certificate after you finish.'}
                    {fmDeadline ? <> Deadline: <strong>{fmtDate(fmDeadline)}</strong></> : null}
                  </p>
                  </div>
                  <CheckBadgeIcon className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-sm text-gray-700 mt-3">
                  You can take the course online at MoneySharp. If this is a joint case, upload both certificates at the same time.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href="https://moneysharp.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100">
                    Go to MoneySharp
                  </a>
                  <Link to="/client/dashboard/documents" className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700">
                    Upload Certificate
                  </Link>
                </div>
              </div>
            

            {/* Duty: Attend 341 meeting */}
            <div className="relative p-5 bg-white rounded-lg shadow border-l-4 border-green-600">
              {meetingShowsComplete && (
                <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                  <CheckCircleIcon className="h-12 w-12 text-green-700" />
                </div>
              )}

              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Attend 341 Meeting</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {meetingDate ? (
                      <>Date/Time: <strong>{fmtDateTime(meetingDate)}</strong></>
                    ) : (
                      <>We will provide the date/time as soon as it is available.</>
                    )}
                  </p>

                  {meetingIsFutureAdmin && (
                    <p className="mt-2 text-sm font-semibold text-green-800">
                      This meeting date is purely administrative and you are not required to attend
                    </p>
                  )}
                </div>
                <UserIcon className="h-6 w-6 text-green-600" />
              </div>

              {(meetingId || meetingPasscode) && (
                <div className="text-sm text-gray-700 mt-3 space-y-1">
                  {meetingId && <div><strong>Meeting ID:</strong> {meetingId}</div>}
                  {meetingPasscode && <div><strong>Passcode:</strong> {meetingPasscode}</div>}
                </div>
              )}

              <div className="mt-3">
                <a
                  href="https://zoom.us/download"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                >
                  Download Zoom
                </a>
              </div>

              <p className="text-sm text-gray-700 mt-3">
                You will be placed under oath and asked to verify the authenticity of your forms.
              </p>
            </div>
            {/* Duty: Reaffirmations (Chapter 7 only, only if there are items) */}
            {chapter === '7' && hasReaffirmations && (
            <div className="relative p-5 bg-white rounded-lg shadow border-l-4 border-green-600">
              {reaffAllCompleted && (
                <div className="absolute inset-0 bg-green-100/75 flex items-center justify-center rounded-lg">
                  <CheckCircleIcon className="h-12 w-12 text-green-700" />
                </div>
              )}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Sign Reaffirmation Agreement(s)</h3>
                                      <p className="text-sm text-gray-600 mt-1">
                    <strong>{reaffAwaitingCreditorCount}</strong> awaiting from creditor •{' '}
                    <strong>{reaffAwaitingSignatureCount}</strong> awaiting your signature •{' '}
                    <strong>{reaffCompletedCount}</strong> completed
                    {reaffirmationDeadline ? <> • Deadline: <strong>{fmtDate(reaffirmationDeadline)}</strong></> : null}
                  </p>
                  </div>
                  <DocumentCheckIcon className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-sm text-gray-700 mt-3">
                  Reaffirmations are only needed if you choose to keep certain secured debts (like a car loan).
                </p>

                <div className="mt-4 space-y-4 text-sm text-gray-700">
                  {reaffAwaitingCreditor.length > 0 && (
                    <div>
                      <div className="font-semibold text-gray-800">Awaiting from creditor</div>
                      <ul className="mt-2 space-y-2">
                        {reaffAwaitingCreditor.map(r => (
                        <li key={r.id} className="rounded border border-gray-200 bg-gray-50 px-3 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-medium text-gray-900">{cleanCreditorName(r.creditor_name) || 'Creditor'}</div>
                              <div className="text-xs text-gray-500">Status: {r.status}</div>
                              {r.signers_required && (
                                <div className="text-xs text-gray-500">Signers: {r.signers_required}</div>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-2 justify-end">
  
                                {attIdByKind(r, "agreement") && (
                                  <button
                                    onClick={() => openPresigned(attIdByKind(r, "agreement"))}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                  >
                                    Download Agreement
                                  </button>
                                )}
                                {attIdByKind(r, "signature_pages") && (
                                  <button
                                    onClick={() => openPresigned(attIdByKind(r, "signature_pages"))}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                  >
                                    Signature Pages
                                  </button>
                                )}
                            </div>
                          </div>
                        </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {reaffAwaitingSignature.length > 0 && (
                    <div>
                      <div className="font-semibold text-gray-800">Awaiting your signature</div>
                      <ul className="mt-2 space-y-2">
                        {reaffAwaitingSignature.map(r => (
                        <li key={r.id} className="rounded border border-indigo-200 bg-indigo-50 px-3 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-medium text-gray-900">{cleanCreditorName(r.creditor_name) || 'Creditor'}</div>
                              <div className="text-xs text-gray-500">Status: {r.status}</div>
                              {r.signers_required && (
                                <div className="text-xs text-gray-500">Signers: {r.signers_required}</div>
                              )}
                              {String(r.status || '').toLowerCase() === 'rejected' && r.rejection_reason && (
                                <div className="mt-1 text-xs text-red-700">
                                  Rejected: {r.rejection_reason}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-2 justify-end">
                              {attIdByKind(r, "agreement") && (
                                <button
                                  onClick={() => openPresigned(attIdByKind(r, "agreement"))}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                >
                                  Download Agreement
                                </button>
                              )}
                              {attIdByKind(r, "signature_pages") && (
                                <button
                                  onClick={() => openPresigned(attIdByKind(r, "signature_pages"))}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                >
                                  Signature Pages
                                </button>
                              )}

                              {/* Upload ONLY when awaiting signature */}
                              {getUploadPath(r) && (
                                <Link
                                  to={getUploadPath(r)}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700"
                                >
                                  Upload Signed
                                </Link>
                              )}
                            </div>
                          </div>
                        </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {reaffCompleted.length > 0 && (
                    <div>
                      <div className="font-semibold text-gray-800">Completed</div>
                      <ul className="mt-2 space-y-2">
                        {reaffCompleted.map(r => (
                          <li key={r.id} className="rounded border border-green-200 bg-green-50 px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-medium text-gray-900">{cleanCreditorName(r.creditor_name) || 'Creditor'}</div>
                                <div className="text-xs text-gray-500">Status: {r.status}</div>
                                {r.signers_required && (
                                  <div className="text-xs text-gray-500">Signers: {r.signers_required}</div>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-2 justify-end">
                                {attIdByKind(r, "agreement") && (
                                  <button
                                    onClick={() => openPresigned(attIdByKind(r, "agreement"))}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                  >
                                    Download Agreement
                                  </button>
                                )}
                                {attIdByKind(r, "signature_pages") && (
                                  <button
                                    onClick={() => openPresigned(attIdByKind(r, "signature_pages"))}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                  >
                                    Signature Pages
                                  </button>
                                )}
                                {attIdByKind(r, "client_signed") && (
                                  <button
                                    onClick={() => openPresigned(attIdByKind(r, "client_signed"))}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                  >
                                    Client Upload
                                  </button>
                                )}
                                {attIdByKind(r, "final_signed") && (
                                  <button
                                    onClick={() => openPresigned(attIdByKind(r, "final_signed"))}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-white text-gray-800 border border-gray-200 hover:bg-gray-100"
                                  >
                                    Final Signed
                                  </button>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Duty: Chapter 13 plan payments */}
            {chapter === '13' && (
              <div className="p-5 bg-white rounded-lg shadow border-l-4 border-green-600">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Start Making Plan Payments</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Your first plan payment is due within 30 days of filing.
                    </p>
                  </div>
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                </div>

                              {ch13Plan && (
  <div className="text-sm text-gray-700 mt-3 space-y-1">
    {ch13Plan.plan_length != null && (
      <div><strong>Plan duration:</strong> {ch13Plan.plan_length} months</div>
    )}

    {firstPlanPaymentDeadline && (
      <div>
        <strong>First payment due:</strong> {fmtDate(firstPlanPaymentDeadline)}
      </div>
    )}

    {ch13Plan.payment_debtor_1 != null && (
      <div>
        <strong>Debtor 1 payment:</strong> ${Number(ch13Plan.payment_debtor_1).toFixed(2)} {periodPhrase(ch13Plan.payment_period_debtor_1)}
        {ch13Plan.payment_source_debtor_1 ? ` • source: ${sourceLabel(ch13Plan.payment_source_debtor_1)}` : ''}
      </div>
    )}

    {String(ch13Plan.payment_source_debtor_1 || '').toLowerCase() === 'payroll' && (
      <div className="text-xs text-gray-600">
        If your plan payment is by payroll, check your paystubs. If it does not start coming out before the deadline, make a payment through TFS.
      </div>
    )}

    {ch13Plan.payment_debtor_2 != null && (
      <div>
        <strong>Debtor 2 payment:</strong> ${Number(ch13Plan.payment_debtor_2).toFixed(2)} {periodPhrase(ch13Plan.payment_period_debtor_2)}
        {ch13Plan.payment_source_debtor_2 ? ` • source: ${sourceLabel(ch13Plan.payment_source_debtor_2)}` : ''}
      </div>
    )}

    {['all', 'none', 'other'].includes(String(ch13Plan.refund_payment || '').toLowerCase()) && (
      <div>
        <strong>Tax refunds:</strong>{' '}
        {String(ch13Plan.refund_payment || '').toLowerCase() === 'all'
          ? 'All refunds go into the plan'
          : String(ch13Plan.refund_payment || '').toLowerCase() === 'none'
            ? 'You keep your refunds'
            : `Other — ${ch13Plan.refund_payment_other_description || ''}`}
      </div>
    )}
  </div>
)}

                <div className="mt-3">
                  <a href="https://www.tfsbillpay.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700">
                    Pay at TFSBillPay
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}